/* ============================================================================
 * BuzzIslands Cloud Helper — Runtime (Phase 2.8)
 *
 * Scope (Phase 2):
 *   - Phase 1 visual foundation.
 *   - Local keyword / intent matching (Greek + English + Greeklish).
 *   - Local actions for cart, checkout, menu, search, categories, product info,
 *     and Buzz Market basics.
 *   - Target highlighting / helper travel-to-target behavior.
 *   - Safe AI launcher gate: the Cloud Helper covers the AI launcher and
 *     opens it only from the "Find products" action.
 *
 * Still OUT of scope:
 *   - AI iframe internals / chat content manipulation.
 *   - AI handoff / clipboard.
 *   - Full Buzz Market tutorial / idle nudges / analytics.
 * ============================================================================ */
(function () {
  'use strict';

  /* ── 0. URL kill switch ──────────────────────────────────────────────── */
  try {
    var p = new URLSearchParams(window.location.search);
    if (p.get('cloudhelper') === '0') {
      var dead = document.getElementById('bzch-root');
      if (dead && dead.parentNode) dead.parentNode.removeChild(dead);
      return;
    }
  } catch (e) { /* keep going */ }

  /* ── 0.1. Feature switches ─────────────────────────────────────────────
   * Local text input is kept in the code but hidden for the live theme until
   * the rule/AI answer flow is ready. Switch this to true to show it again.
   * ───────────────────────────────────────────────────────────────────── */
  var ENABLE_LOCAL_HELPER_INPUT = false;

  function isLocalHelperInputEnabled() {
    return ENABLE_LOCAL_HELPER_INPUT === true;
  }

  /* ── 1. Utilities ────────────────────────────────────────────────────── */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function getConfig() {
    var el = document.getElementById('buzz-cloud-config');
    if (!el) return {};
    try { return JSON.parse(el.textContent || '{}'); } catch (e) { return {}; }
  }

  function content() { return window.BuzzCloudHelperContent || null; }
  function intents() { return window.BuzzCloudHelperIntents || null; }

  function t(path, fallback) {
    var c = content();
    if (!c || typeof c.t !== 'function') return (fallback != null) ? fallback : '';
    return c.t(path, fallback);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  function defer(fn, ms) { return window.setTimeout(fn, ms == null ? 0 : ms); }

  function isVisible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    var st = window.getComputedStyle ? window.getComputedStyle(el) : null;
    return !st || (st.display !== 'none' && st.visibility !== 'hidden' && parseFloat(st.opacity || '1') > 0.01);
  }

  function safeClick(el) {
    if (!el || typeof el.click !== 'function') return false;
    try { el.click(); return true; } catch (e) { return false; }
  }

  function setupAIClickBlock() {
    if (!S.aiClickBlock) return;
    function swallow(ev) {
      if (!ev) return false;
      try { ev.preventDefault(); } catch (e) {}
      try { ev.stopPropagation(); } catch (e) {}
      try { if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation(); } catch (e) {}
      return false;
    }
    ['pointerdown', 'pointerup', 'click', 'touchstart', 'touchend'].forEach(function (type) {
      try { S.aiClickBlock.addEventListener(type, swallow, { capture: true, passive: false }); } catch (e) {
        try { S.aiClickBlock.addEventListener(type, swallow, true); } catch (e2) {}
      }
    });
  }

  /* ── 2. Page → chip plan ─────────────────────────────────────────────── */
  function chip(id, action, target, icon) {
    return { id: id, action: action, target: target, icon: icon };
  }

  var CHIPS_BY_PAGE = {
    index: [
      chip('account',        'guided-activate', 'account',         '👤'),
      chip('showCategories', 'guided-activate', 'menu',            '🗂️'),
      chip('cartLink',       'guided-activate', 'cart',            '🛒'),
      chip('findProducts',   'open-ai',         '',                '🤖')
    ],
    collection: [
      chip('showPopular',    'intent', 'nav.all',          '✨'),
      chip('tryInMarket',    'intent', 'game.open',        '🎮'),
      chip('openSearch',     'intent', 'search.open',      '🔍')
    ],
    product: [
      chip('showIngredients','intent', 'product.info',     '📋'),
      chip('showNutrition',  'intent', 'product.nutrition','🥗'),
      chip('addToCartHelp',  'intent', 'product.addtocart','🛒')
    ],
    search: [
      chip('marketProductsCart', 'market-menu',     'products', '🛒'),
      chip('account',            'guided-activate', 'account',   '👤'),
      chip('showCategories',     'guided-activate', 'menu',      '🗂️'),
      chip('findProducts',       'open-ai',         '',          '🤖')
    ],
    cartEmpty: [
      chip('continueShopping','go-home-categories','',     '🛍️'),
      chip('findProducts',   'open-ai',         '',        '🤖')
    ],
    cartFull: [
      chip('cartCheckout',   'guided-checkout', 'checkout','🔒'),
      chip('continueShopping','go-home-categories','',     '🛍️'),
      chip('findProducts',   'open-ai',         '',        '🤖')
    ],
    market: [
      chip('marketHowPlay',        'market-menu', 'play',       '🎯'),
      chip('marketProductsCart',   'market-menu', 'products',   '🛒'),
      chip('marketOpenCategories', 'market-open-menu', '',      '🗂️')
    ],
    policy: [
      chip('contactUs',      'intent', 'support.contact',  '💬'),
      chip('openMarket',     'intent', 'game.open',        '🎮')
    ],
    fallback: [
      chip('openMarket',     'intent', 'game.open',        '🎮'),
      chip('showCategories', 'intent', 'menu.categories',  '🗂️'),
      chip('cartLink',       'intent', 'cart.locate',      '🛒')
    ]
  };


  var MARKET_MENUS = {
    play: {
      titlePath: 'marketMenus.play.title',
      items: [
        chip('marketForward',        'intent', 'market.forward',  '⬆️'),
        chip('marketExit',           'intent', 'market.exit',     '🚪')
      ]
    },
    products: {
      titlePath: 'marketMenus.products.title',
      items: [
        chip('marketViewProduct', 'intent', 'market.viewProduct', '👁️'),
        chip('marketAddProduct',  'intent', 'market.addProduct',  '➕'),
        chip('marketPrice',       'intent', 'market.price',       '💶'),
        chip('marketCart',        'intent', 'cart.locate',        '🛒')
      ]
    }
  };

  function selectChips(cfg) {
    var template = String(cfg.template || '');
    var pageType = String(cfg.pageType || '');
    if (template === 'page.buzz-market') return CHIPS_BY_PAGE.market;
    if (pageType === 'product') return CHIPS_BY_PAGE.product;
    if (pageType === 'collection' || pageType === 'list-collections') return CHIPS_BY_PAGE.collection;
    if (pageType === 'search') return CHIPS_BY_PAGE.search;
    if (pageType === 'cart') return getCartCount() > 0 ? CHIPS_BY_PAGE.cartFull : CHIPS_BY_PAGE.cartEmpty;
    if (pageType === 'index') return CHIPS_BY_PAGE.index;
    if (pageType === 'policy' || pageType === 'page' || pageType === 'article' || pageType === 'blog') return CHIPS_BY_PAGE.policy;
    return CHIPS_BY_PAGE.fallback;
  }

  function selectGreeting(cfg) {
    var template = String(cfg.template || '');
    var pageType = String(cfg.pageType || '');
    var key = 'default';
    if (template === 'page.buzz-market') key = 'market';
    else if (pageType === 'index') key = 'homepage';
    else if (pageType === 'product') key = 'product';
    else if (pageType === 'collection' || pageType === 'list-collections') key = 'collection';
    else if (pageType === 'search') key = 'search';
    else if (pageType === 'cart') key = getCartCount() > 0 ? 'cartFull' : 'cartEmpty';
    else if (pageType === 'policy' || pageType === 'page' || pageType === 'article' || pageType === 'blog') key = 'policy';
    return t('greetings.' + key, '');
  }

  function getCartCount() {
    var selectors = ['#bzc-count', '.cart-count', '[data-cart-count]'];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (!el) continue;
      var text = String(el.textContent || '').replace(/[^0-9]/g, '');
      if (text !== '') {
        var n = parseInt(text, 10);
        return isNaN(n) ? 0 : n;
      }
    }
    return 0;
  }

  /* ── 3. State ────────────────────────────────────────────────────────── */
  var S = {
    isOpen: false,
    config: null,
    root: null,
    button: null,
    panel: null,
    form: null,
    input: null,
    send: null,
    inputLabel: null,
    greetingEl: null,
    tipEl: null,
    chipsEl: null,
    closeEl: null,
    lastFocus: null,
    keydownBound: false,
    originalParent: null,
    originalNextSibling: null,
    fullscreenBound: false,
    highlightedEl: null,
    highlightedEls: [],
    highlightOverlays: [],
    pointingTimer: null,
    guideEl: null,
    guideTextEl: null,
    guideCountEl: null,
    guideTickTimer: null,
    guideUntil: 0,
    drawerHideObserver: null,
    drawerHideTimer: null,
    aiLauncher: null,
    aiObserver: null,
    aiTimer: null,
    aiSyncQueued: false,
    aiClickBlock: null,
    introEl: null,
    introTimer: null,
    aiTuneEl: null,
    marketMountObserver: null,
    marketMountQueued: false
  };

  /* ── 4. Init ─────────────────────────────────────────────────────────── */
  function init() {
    S.root = document.getElementById('bzch-root');
    if (!S.root) return;
    S.config = getConfig();

    if (!content() || typeof content().t !== 'function') {
      try { S.root.parentNode.removeChild(S.root); } catch (e) {}
      return;
    }

    S.button = $('#bzch-button', S.root);
    S.panel = $('#bzch-panel', S.root);
    S.form = $('#bzch-form', S.root);
    S.input = $('#bzch-input', S.root);
    S.send = $('#bzch-send', S.root);
    S.inputLabel = $('#bzch-input-label', S.root);
    S.greetingEl = $('#bzch-greeting', S.panel);
    S.tipEl = $('#bzch-tip', S.panel);
    S.chipsEl = $('#bzch-chips', S.panel);
    S.closeEl = $('[data-bzch-close]', S.panel);
    S.aiClickBlock = $('.bzch__ai-clickblock', S.root);
    ensureGuideBubble();
    applyLocalInputFeatureSwitch();

    if (!S.button || !S.panel) return;

    try {
      if (sessionStorage.getItem('bzch_dismissed') === '1') S.root.classList.add('bzch--opened-once');
    } catch (e) {}

    localizeStaticUi();

    S.button.addEventListener('click', toggle);
    window.addEventListener('bw:help', function () {
      if (!document.getElementById('bwRoot')) return;
      S.root.classList.add('bzch--world-help');
      toggle();
    });
    setupAIClickBlock();
    if (S.closeEl) S.closeEl.addEventListener('click', close);
    if (isLocalHelperInputEnabled() && S.form) S.form.addEventListener('submit', onSubmit);
    setupInputFocusSafety();
    setupPanelScrollSafety();

    if (!S.keydownBound) {
      document.addEventListener('keydown', onKeyDown);
      S.keydownBound = true;
    }

    schedulePositionCheck();
    window.addEventListener('resize', schedulePositionCheck, { passive: true });
    window.addEventListener('orientationchange', schedulePositionCheck, { passive: true });
    if (window.visualViewport && typeof window.visualViewport.addEventListener === 'function') {
      window.visualViewport.addEventListener('resize', schedulePositionCheck);
    }

    setupSuppressionObserver();
    setupFullscreenMounting();
    setupAIGate();
    defer(showIntroBubble, 2800);
    defer(handleAutoOpenRequests, 350);
  }

  function localizeStaticUi() {
    var openLabel = t('a11y.helperButtonLabel', '');
    var closeLabel = t('a11y.helperCloseLabel', '');
    var chipsLabel = t('a11y.chipListLabel', '');
    var inputLabel = t('a11y.inputLabel', '');
    var submitLabel = t('a11y.submitLabel', '');

    if (S.button && openLabel) S.button.setAttribute('aria-label', openLabel);
    if (S.closeEl && closeLabel) S.closeEl.setAttribute('aria-label', closeLabel);
    if (S.chipsEl && chipsLabel) S.chipsEl.setAttribute('aria-label', chipsLabel);
    if (S.inputLabel) S.inputLabel.textContent = inputLabel;
    if (S.input) {
      S.input.setAttribute('aria-label', inputLabel);
      S.input.setAttribute('placeholder', t('input.placeholder', ''));
    }
    if (S.send) {
      S.send.textContent = t('buttons.send', '');
      if (submitLabel) S.send.setAttribute('aria-label', submitLabel);
    }
  }


  function applyLocalInputFeatureSwitch() {
    var enabled = isLocalHelperInputEnabled();
    if (S.root) S.root.classList.toggle('bzch--input-disabled', !enabled);
    if (S.form) {
      S.form.hidden = !enabled;
      S.form.setAttribute('aria-hidden', enabled ? 'false' : 'true');
    }
    if (S.input) {
      S.input.disabled = !enabled;
      if (!enabled) S.input.setAttribute('tabindex', '-1');
      else S.input.removeAttribute('tabindex');
    }
    if (S.send) {
      S.send.disabled = !enabled;
      if (!enabled) S.send.setAttribute('tabindex', '-1');
      else S.send.removeAttribute('tabindex');
    }
  }


  function setupInputFocusSafety() {
    if (!isLocalHelperInputEnabled()) return;
    if (!S.input) return;

    /* Mobile keyboard safety: the guide mode moves the helper around and the
       wrapper uses pointer-events:none. These handlers keep taps on the input
       simple and never let a leftover guide/target state steal the focus. */
    var keepFocus = function () {
      if (S.root) S.root.classList.remove('bzch--drawer-hidden');
      if (S.root && S.root.classList.contains('bzch--pointing')) clearPointing();
    };

    S.input.addEventListener('pointerdown', function (e) { e.stopPropagation(); keepFocus(); });
    S.input.addEventListener('touchstart', function (e) { e.stopPropagation(); keepFocus(); }, { passive: true });
    S.input.addEventListener('click', function (e) {
      e.stopPropagation();
      keepFocus();
      try { S.input.focus({ preventScroll: true }); } catch (err) { try { S.input.focus(); } catch (_) {} }
    });
  }

  /* ── 5. Open / close ────────────────────────────────────────────────── */
  function open() {
    if (S.isOpen) return;
    hideIntroBubble();
    S.isOpen = true;
    S.lastFocus = document.activeElement;
    clearPointing();
    buildPanelContent();
    S.panel.hidden = false;
    S.button.setAttribute('aria-expanded', 'true');
    S.root.classList.add('bzch--open', 'bzch--opened-once');
    try { sessionStorage.setItem('bzch_dismissed', '1'); } catch (e) {}
    defer(function () { if (S.closeEl) S.closeEl.focus({ preventScroll: true }); }, 30);
  }

  function close() {
    if (!S.isOpen) return;
    S.isOpen = false;
    S.panel.hidden = true;
    S.button.setAttribute('aria-expanded', 'false');
    S.root.classList.remove('bzch--open', 'bzch--world-help');
    try { sessionStorage.setItem('bzch_dismissed', '1'); } catch (e) {}
    clearPointing();
    try {
      if (S.lastFocus && typeof S.lastFocus.focus === 'function' && document.body.contains(S.lastFocus)) S.lastFocus.focus();
      else if (S.button) S.button.focus();
    } catch (e) {}
  }

  function toggle() { if (S.isOpen) close(); else open(); }

  function onKeyDown(e) {
    if (e.key === 'Escape' && S.isOpen) {
      e.stopPropagation();
      close();
    }
  }

  /* ── 6. Panel content builder ───────────────────────────────────────── */
  function buildPanelContent() {
    if (!S.panel) return;
    localizeStaticUi();
    if (S.greetingEl) S.greetingEl.textContent = selectGreeting(S.config);
    if (S.tipEl) S.tipEl.textContent = '';
    if (S.input) S.input.value = '';
    renderChips(selectChips(S.config));
  }

  function renderChips(chips) {
    if (!S.chipsEl) return;
    while (S.chipsEl.firstChild) S.chipsEl.removeChild(S.chipsEl.firstChild);
    for (var i = 0; i < chips.length; i++) {
      var el = renderChip(chips[i]);
      if (el) S.chipsEl.appendChild(el);
    }
  }

  function renderChip(cfg) {
    var label = t('buttons.' + cfg.id, '');
    if (!label) return null;

    var el;
    if (cfg.action === 'nav' && cfg.target) {
      el = document.createElement('a');
      el.href = cfg.target;
    } else if (cfg.action === 'nav-here') {
      el = document.createElement('a');
      el.href = window.location.pathname + window.location.search;
    } else if (cfg.action === 'nav-market') {
      el = document.createElement('a');
      el.href = computeMarketHref();
    } else {
      el = document.createElement('button');
      el.type = 'button';
    }

    el.className = 'bzch__chip';
    el.setAttribute('role', 'listitem');
    el.dataset.bzchChip = cfg.id;
    el.dataset.bzchAction = cfg.action;
    if (cfg.target) el.dataset.bzchTarget = cfg.target;

    if (cfg.icon) {
      var iconEl = document.createElement('span');
      iconEl.className = 'bzch__chip-icon';
      iconEl.setAttribute('aria-hidden', 'true');
      iconEl.textContent = cfg.icon;
      el.appendChild(iconEl);
    }

    var labelEl = document.createElement('span');
    labelEl.className = 'bzch__chip-label';
    labelEl.textContent = label;
    el.appendChild(labelEl);

    el.addEventListener('click', function (ev) { handleChipClick(ev, cfg); });
    return el;
  }


  function showMarketMenu(menuId) {
    var menu = MARKET_MENUS[menuId];
    if (!menu) return;
    if (S.tipEl) S.tipEl.textContent = t(menu.titlePath, '');
    renderMarketMenuChips(menu.items);
  }

  function renderMarketMenuChips(items) {
    if (!S.chipsEl) return;
    while (S.chipsEl.firstChild) S.chipsEl.removeChild(S.chipsEl.firstChild);

    var back = renderChip({ id: 'marketBackToMain', action: 'market-main', target: '', icon: '←' });
    if (back) {
      back.classList.add('bzch__chip--back');
      S.chipsEl.appendChild(back);
    }

    for (var i = 0; i < items.length; i++) {
      var el = renderChip(items[i]);
      if (el) S.chipsEl.appendChild(el);
    }
  }

  function handleChipClick(event, cfg) {
    if (cfg.action === 'market-menu') {
      event.preventDefault();
      showMarketMenu(cfg.target);
      return;
    }

    if (cfg.action === 'market-main') {
      event.preventDefault();
      renderChips(selectChips(S.config));
      if (S.tipEl) S.tipEl.textContent = '';
      return;
    }

    if (cfg.action === 'market-open-menu') {
      event.preventDefault();
      openMarketMenu('responses.menuOpen');
      return;
    }

    if (cfg.action === 'guided-activate') {
      event.preventDefault();
      return guidedActivate(cfg.target);
    }

    if (cfg.action === 'guided-checkout') {
      event.preventDefault();
      return guidedCheckout();
    }

    if (cfg.action === 'go-home-categories') {
      event.preventDefault();
      return navigateHomeWithCategories();
    }

    if (cfg.action === 'open-ai') {
      event.preventDefault();
      return openAIHelper();
    }

    if (cfg.action === 'intent') {
      event.preventDefault();
      var result = matchPresetIntent(cfg.target);
      executeIntent(result);
      return;
    }

    if (cfg.action === 'nav' || cfg.action === 'nav-here' || cfg.action === 'nav-market') {
      if (event.currentTarget && event.currentTarget.tagName === 'A') return;
      event.preventDefault();
      var href = (cfg.action === 'nav-market') ? computeMarketHref() : (cfg.action === 'nav-here') ? (window.location.pathname + window.location.search) : (cfg.target || '/');
      window.location.assign(href);
      return;
    }

    if (cfg.action === 'click' && cfg.target) {
      event.preventDefault();
      var target = $(cfg.target);
      if (target && safeClick(target)) close();
      return;
    }

    if (cfg.action === 'tip') {
      event.preventDefault();
      showTip(cfg.target);
    }
  }


  function setupPanelScrollSafety() {
    if (!S.panel || S.panel.dataset.bzchScrollSafe === '1') return;
    S.panel.dataset.bzchScrollSafe = '1';

    /* The Buzz Market canvas uses aggressive touch handling. Keep vertical
       scrolling inside the helper panel itself and do not let the gesture leak
       to the Three.js canvas behind it. Do not preventDefault — the panel still
       needs native momentum scrolling on mobile. */
    function stop(e) { e.stopPropagation(); }
    S.panel.addEventListener('touchstart', stop, { passive: true });
    S.panel.addEventListener('touchmove', stop, { passive: true });
    S.panel.addEventListener('wheel', stop, { passive: true });
  }

  function onSubmit(e) {
    e.preventDefault();
    if (!isLocalHelperInputEnabled()) return;
    var value = S.input ? S.input.value : '';
    if (!value || !String(value).trim()) {
      showTip('input.empty');
      return;
    }
    var engine = intents();
    var result = engine && typeof engine.match === 'function' ? engine.match(value, getPageContext()) : null;
    executeIntent(result || { action: 'SHOW_TIP', messagePath: 'responses.unknown' });
  }

  function matchPresetIntent(id) {
    var engine = intents();
    if (!engine || !Array.isArray(engine.intents)) return { action: 'SHOW_TIP', messagePath: 'responses.unknown' };
    for (var i = 0; i < engine.intents.length; i++) {
      if (engine.intents[i].id === id) {
        var base = engine.intents[i];
        var out = {};
        for (var k in base) if (Object.prototype.hasOwnProperty.call(base, k)) out[k] = base[k];
        out.intent = base.id;
        if (base.category && engine.categories) {
          out.categoryData = engine.categories[base.category] || null;
          if (!out.messagePath && out.categoryData && out.categoryData.response) out.messagePath = out.categoryData.response;
        }
        return out;
      }
    }
    return { action: 'SHOW_TIP', messagePath: 'responses.unknown' };
  }

  function showTip(pathOrText) {
    if (!S.tipEl) return;
    var text = String(pathOrText || '');
    if (text.indexOf('.') !== -1) text = t(text, text);
    S.tipEl.textContent = text;
  }

  function getPageContext() {
    return {
      pageType: S.config ? S.config.pageType : '',
      template: S.config ? S.config.template : '',
      productHandle: S.config ? S.config.productHandle : '',
      collectionHandle: S.config ? S.config.collectionHandle : '',
      isMarket: isMarketPage()
    };
  }

  /* ── 7. Actions ─────────────────────────────────────────────────────── */
  function executeIntent(result) {
    if (!result) return showTip('responses.unknown');

    var action = result.action || 'SHOW_TIP';
    var messagePath = result.messagePath || 'responses.unknown';

    if (action === 'SHOW_MARKET_MENU') return showMarketMenu(result.menu || 'play');
    if (action === 'POINT_TO_CART') return pointToCart(messagePath);
    if (action === 'POINT_TO_CHECKOUT') return pointToCheckout(messagePath);
    if (action === 'POINT_TO_ACCOUNT') return pointToTarget(resolveTarget('account'), messagePath);
    if (action === 'POINT_TO_LANGUAGE') return pointToTarget(resolveTarget('language'), messagePath);
    if (action === 'POINT_TO_MENU') return pointToTarget(resolveTarget('menu'), messagePath);
    if (action === 'POINT_TO_SEARCH') return pointToTarget(resolveTarget('search'), messagePath);
    if (action === 'POINT_TO_ADD_TO_CART') return isMarketPage() ? pointToMarketAdd(messagePath) : pointToTarget(resolveTarget('addToCart'), messagePath);
    if (action === 'POINT_TO_PRODUCT_INFO') return isMarketPage() ? pointToMarketView(messagePath) : pointToProductInfo(messagePath);
    if (action === 'POINT_TO_MARKET_CONTROLS') return pointToMarketControls(messagePath);
    if (action === 'POINT_TO_MARKET_UP') return pointToTarget(resolveTarget('marketUp'), messagePath);
    if (action === 'POINT_TO_MARKET_DOWN') return pointToTarget(resolveTarget('marketDown'), messagePath);
    if (action === 'POINT_TO_MARKET_EXIT') return pointToTarget(resolveTarget('marketExit'), messagePath);
    if (action === 'POINT_TO_MARKET_PRICE') return pointToMarketPrice(messagePath);
    if (action === 'POINT_TO_MARKET_VIEW') return pointToMarketView(messagePath);
    if (action === 'POINT_TO_MARKET_ADD') return pointToMarketAdd(messagePath);
    if (action === 'OPEN_MARKET_MENU') return openMarketMenu(messagePath);
    if (action === 'POINT_TO_MARKET_PRODUCT') return pointToMarketView(messagePath);
    if (action === 'OPEN_MENU') return openMenu(messagePath);
    if (action === 'OPEN_SEARCH') return openSearch(messagePath);
    if (action === 'OPEN_MARKET') return navigateWithTip(localizeInternalHref('/pages/buzz-market'), messagePath);
    if (action === 'NAV_HOME') return navigateHome();
    if (action === 'NAV') return navigateWithTip(safeHref(result.href || '/', result.fallbackHref), messagePath);
    if (action === 'NAV_CATEGORY') return handleCategory(result);
    if (action === 'SHOW_OPTIONS') return showOptions(result);
    if (action === 'SHOW_SENSITIVE') return showTip(messagePath);
    return showTip(messagePath);
  }

  // Buzz World: the cart lives inside the game — open it there directly.
  function openWorldCart() {
    if (!document.getElementById('bwRoot') || !window.bwCart || typeof window.bwCart.open !== 'function') return false;
    clearPointing();
    close();
    window.bwCart.open();
    return true;
  }

  function pointToCart(messagePath) {
    if (openWorldCart()) return;
    var target = resolveTarget('cart');
    if (target) {
      if (!isMarketPage()) return pointToVendingCart(target, messagePath);
      return pointToTarget(target, messagePath);
    }
    showTip('responses.cartMissing');
  }

  function pointToCheckout(messagePath) {
    if (openWorldCart()) return;
    var target = resolveTarget('checkout');
    if (target) return pointToTarget(target, messagePath);
    navigateWithTip(localizeInternalHref('/cart'), 'responses.checkoutGoCart');
  }


  function guidedActivate(name) {
    name = String(name || '');
    if (name === 'account') return guidedActivateTarget(resolveTarget('account'), 'guide.account', function () {
      var account = resolveTarget('account');
      if (account && safeClick(account)) return;
      window.location.assign(localizeInternalHref('/account'));
    });
    if (name === 'menu') return guidedActivateTarget(resolveTarget('menu'), 'guide.menu', function () {
      var menu = resolveTarget('menu');
      if (menu) safeClick(menu);
      hideHelperWhileDrawerOpen(isMarketPage() ? '#bm3dMenuDrawer' : '#menuDrawer');
    });
    if (name === 'cart' && openWorldCart()) return;
    if (name === 'cart') return guidedActivateTarget(resolveTarget('cart'), 'guide.cart', function () {
      var cart = resolveTarget('cart');
      if (cart && safeClick(cart)) return;
      window.location.assign(localizeInternalHref('/cart'));
    });
  }

  function guidedCheckout() {
    if (openWorldCart()) return;
    return guidedActivateTarget(resolveTarget('checkout'), 'guide.checkout', function () {
      var checkout = resolveTarget('checkout');
      if (checkout && safeClick(checkout)) return;
      window.location.assign(localizeInternalHref('/checkout'));
    });
  }

  function navigateHomeWithCategories() {
    closePanelForGuide();
    clearPointing();
    var href = getLocalizedRoot() + '?openCategories=1';
    window.location.assign(href);
  }

  function getLocalizedRoot() {
    try {
      var path = window.location.pathname || '/';
      if (/^\/el(?:\/|$)/.test(path)) return '/el';
    } catch (e) {}
    return '/';
  }


  function localizeInternalHref(href) {
    href = String(href || '');
    if (!href || href === '#' || /^(?:https?:|mailto:|tel:|sms:)/i.test(href)) return href;
    if (!href.charAt || href.charAt(0) !== '/') return href;
    if (/^\/el(?:\/|$)/.test(href)) return href;
    var root = getLocalizedRoot();
    if (root === '/el') return '/el' + (href === '/' ? '' : href);
    return href;
  }

  function handleAutoOpenRequests() {
    try {
      var params = new URLSearchParams(window.location.search || '');
      if (params.get('openCategories') !== '1') return;
      params.delete('openCategories');
      var clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + (window.location.hash || '');
      if (window.history && typeof window.history.replaceState === 'function') {
        window.history.replaceState({}, '', clean);
      }
      var menu = resolveTarget('menu');
      if (menu) {
        closePanelForGuide();
        clearPointing();
        defer(function () {
          safeClick(menu);
          hideHelperWhileDrawerOpen(isMarketPage() ? '#bm3dMenuDrawer' : '#menuDrawer');
        }, 250);
      }
    } catch (e) {}
  }

  function guidedActivateTarget(target, messagePath, activate) {
    if (!target || !isVisible(target)) {
      if (typeof activate === 'function') activate();
      return;
    }
    clearPointing();
    closePanelForGuide();

    S.highlightedEls = [target];
    if (isMarketExitTarget(target)) createTargetOverlay(target, 'exit');
    else {
      target.classList.add('bzch-target-highlight');
      if (isMarketPage()) target.classList.add('bzch-target-highlight--market');
    }
    S.highlightedEl = target;

    var pos = computePointPosition(target);
    if (pos && S.root) {
      S.root.style.setProperty('--bzch-point-top', pos.top + 'px');
      S.root.style.setProperty('--bzch-point-left', pos.left + 'px');
      S.root.classList.remove('bzch--guide-left', 'bzch--guide-right', 'bzch--guide-top', 'bzch--guide-bottom');
      S.root.classList.add('bzch--pointing', 'bzch--guide-' + (pos.guideSide || 'bottom'));
      startGuideBubble(messagePath, 2);
    }

    defer(function () {
      try { if (typeof activate === 'function') activate(); } catch (e) {}
      defer(function () { clearPointing(); }, 450);
    }, 1200);
  }

  function openAIHelper() {
    /* The AI launcher lives directly behind the Cloud Helper. Do not try to
       control the AI iframe. Close our panel, briefly remove the helper from
       hit-testing, then click the real element behind the helper, exactly like a
       customer would have clicked the AI bubble if the helper were not covering it. */
    if (isMarketPage()) return showTip('responses.aiOpenMissing');

    syncAIGateNow();
    closePanelForGuide();
    clearPointing();

    defer(function () {
      var clicked = clickAIBehindHelper();
      defer(syncAIGateNow, 120);
      defer(syncAIGateNow, 450);
      defer(syncAIGateNow, 900);
      defer(syncAIGateNow, 1600);

      if (!clicked) {
        S.root.classList.remove('bzch--ai-open-hidden');
        showTip('responses.aiOpenMissing');
      }
    }, 220);
  }

  function clickAIBehindHelper() {
    if (!S.root) return false;

    var button = S.root.querySelector('.bzch__button');
    var rect = (button || S.root).getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return false;

    var points = [
      [rect.left + rect.width / 2, rect.top + rect.height / 2],
      [rect.left + rect.width * 0.58, rect.top + rect.height * 0.58],
      [rect.left + rect.width * 0.70, rect.top + rect.height * 0.58],
      [rect.left + rect.width * 0.58, rect.top + rect.height * 0.72],
      [rect.left + rect.width * 0.72, rect.top + rect.height * 0.72],
      [rect.left + rect.width * 0.35, rect.top + rect.height * 0.50],
      [rect.left + rect.width * 0.65, rect.top + rect.height * 0.50],
      [rect.left + rect.width * 0.50, rect.top + rect.height * 0.35],
      [rect.left + rect.width * 0.50, rect.top + rect.height * 0.65]
    ];

    var oldPointer = S.root.style.pointerEvents;
    var oldVisibility = S.root.style.visibility;
    var oldOpacity = S.root.style.opacity;
    S.root.style.pointerEvents = 'none';
    S.root.style.visibility = 'hidden';
    S.root.style.opacity = '0';

    var target = null;
    try {
      for (var i = 0; i < points.length && !target; i++) {
        var x = Math.round(points[i][0]);
        var y = Math.round(points[i][1]);
        var el = document.elementFromPoint(x, y);
        if (el && !isInsideHelper(el)) target = pickClickableAIAncestor(el) || el;
      }
    } catch (e) {}

    S.root.style.pointerEvents = oldPointer;
    S.root.style.visibility = oldVisibility;
    S.root.style.opacity = oldOpacity;

    if (!target || isInsideHelper(target)) return false;

    try {
      target.focus && target.focus();
    } catch (e) {}
    safeClick(target);
    return true;
  }

  function pickClickableAIAncestor(el) {
    var cur = el;
    for (var depth = 0; cur && depth < 7; depth++, cur = cur.parentElement) {
      if (isInsideHelper(cur)) return null;
      var tag = String(cur.tagName || '').toLowerCase();
      if (tag === 'button' || tag === 'a' || tag === 'iframe') return cur;
      if (cur.getAttribute && (cur.getAttribute('role') === 'button' || cur.getAttribute('tabindex') === '0')) return cur;
      var txt = ((cur.getAttribute && (cur.getAttribute('aria-label') || cur.getAttribute('title') || '')) || '').toLowerCase();
      if (txt.indexOf('chat') !== -1 || txt.indexOf('ai') !== -1 || txt.indexOf('assistant') !== -1 || txt.indexOf('jot') !== -1) return cur;
    }
    return null;
  }

  function openMenu(messagePath) {
    var worldCategories = document.getElementById('bwCategories');
    if (worldCategories) { pointToTarget(worldCategories, 'guide.here'); return; }
    if (isMarketPage()) return openMarketMenu(messagePath);
    var target = resolveTarget('menu');
    if (target) {
      closePanelForGuide();
      clearPointing();
      safeClick(target);
      hideHelperWhileDrawerOpen('#menuDrawer');
      return;
    }
    pointToTarget(resolveTarget('menuFallback'), 'guide.here');
  }

  function openMarketMenu(messagePath) {
    var target = resolveTarget('menu');
    if (target) {
      closePanelForGuide();
      clearPointing();
      safeClick(target);
      hideHelperWhileDrawerOpen('#bm3dMenuDrawer');
      return;
    }
    pointToTarget(resolveTarget('menuFallback'), 'guide.here');
  }


  function navigateHome() {
    closePanelForGuide();
    clearPointing();
    var href = getLocalizedRoot();
    window.location.assign(href);
  }

  function isDrawerOpen(drawer) {
    if (!drawer) return false;
    return drawer.classList.contains('is-open') || drawer.getAttribute('aria-hidden') === 'false';
  }

  function hideHelperWhileDrawerOpen(selector) {
    if (!S.root) return;
    var drawer = document.querySelector(selector);
    if (!drawer) return;

    function update() {
      var open = isDrawerOpen(drawer);
      if (S.root) S.root.classList.toggle('bzch--drawer-hidden', open);
      if (!open) cleanup();
    }

    function cleanup() {
      if (S.drawerHideObserver) {
        try { S.drawerHideObserver.disconnect(); } catch (e) {}
        S.drawerHideObserver = null;
      }
      if (S.drawerHideTimer) {
        clearTimeout(S.drawerHideTimer);
        S.drawerHideTimer = null;
      }
      if (S.root) S.root.classList.remove('bzch--drawer-hidden');
    }

    cleanup();
    S.root.classList.add('bzch--drawer-hidden');
    try {
      S.drawerHideObserver = new MutationObserver(update);
      S.drawerHideObserver.observe(drawer, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
    } catch (e) {}
    S.drawerHideTimer = setTimeout(cleanup, 12000);
    setTimeout(update, 120);
  }

  function openSearch(messagePath) {
    var target = resolveTarget('search');
    if (target) {
      closePanelForGuide();
      safeClick(target);
      return defer(function () {
        pointToTarget(resolveTarget('searchInput') || target, 'guide.here');
      }, 220);
    }
    showTip(messagePath);
  }

  function handleCategory(result) {
    var cat = result.categoryData;
    if (!cat) return showTip('responses.categoryOptions');
    var href = resolveCategoryHref(cat);
    showTip(cat.response || result.messagePath || 'responses.categoryOptions');

    if (isMarketPage()) {
      /* Inside Buzz Market, categories are handled by the existing in-game menu.
         Do not duplicate category choices or switch automatically at this stage. */
      return openMarketMenu('responses.menuOpen');
    }

    defer(function () { window.location.assign(localizeInternalHref(href)); }, 260);
  }

  function navigateWithTip(href, messagePath) {
    showTip(messagePath);
    href = safeHref(href, '/search?q=' + encodeURIComponent(String(href || '').replace(/[^a-z0-9]+/gi, ' ')) + '&type=product');
    if (!href) return;
    defer(function () { window.location.assign(localizeInternalHref(href)); }, 260);
  }

  function safeHref(href, fallbackHref) {
    href = String(href || '');
    fallbackHref = String(fallbackHref || '/search?type=product');
    if (!href || href === '#') return fallbackHref;
    /* Avoid old wrong guesses that caused 404s. */
    if (href === '/collections/chocolate') href = '/collections/chocolates';
    if (href === '/collections/all') href = '/collections/all-products';
    return localizeInternalHref(href);
  }

  function resolveCategoryHref(cat) {
    var menuHref = findMenuHref(cat.menuText || [cat.title || '', cat.handle || '']);
    if (menuHref) return safeHref(menuHref, cat.fallbackHref);
    return safeHref(cat.href, cat.fallbackHref);
  }

  function normalizeLookupText(s) {
    var engine = intents();
    if (engine && typeof engine.normalize === 'function') return engine.normalize(s);
    s = String(s == null ? '' : s).toLowerCase();
    if (s.normalize) s = s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    return s.replace(/\s+/g, ' ').trim();
  }

  function findMenuHref(words) {
    words = (words || []).map(normalizeLookupText).filter(Boolean);
    if (!words.length) return null;
    var links = $all('#menuDrawer a[href], .bzmenu a[href], header a[href], a.bzmenu__card[href], a.bzmenu__pill[href]');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      var txt = normalizeLookupText((link.textContent || '') + ' ' + (link.getAttribute('aria-label') || '') + ' ' + (link.getAttribute('href') || ''));
      for (var j = 0; j < words.length; j++) {
        if (txt.indexOf(words[j]) !== -1) return link.getAttribute('href');
      }
    }
    return null;
  }

  function pointToProductInfo(messagePath) {
    var target = resolveTarget('productInfo');
    if (target) return pointToTarget(target, messagePath);

    /* If a product handle exists, ask the existing popup system to show it.
       We call the public global only; we don't modify popup internals. */
    if (S.config && S.config.productHandle && window.BuzzGlobalPopup && typeof window.BuzzGlobalPopup.open === 'function') {
      try {
        window.BuzzGlobalPopup.open(S.config.productHandle, {});
        defer(function () { pointToTarget(resolveTarget('productPopup'), messagePath); }, 350);
        return;
      } catch (e) {}
    }
    showTip(messagePath);
  }

  function visibleAll(selectors) {
    var out = [];
    for (var i = 0; i < selectors.length; i++) {
      var list = $all(selectors[i]);
      for (var j = 0; j < list.length; j++) {
        if (isVisible(list[j]) && out.indexOf(list[j]) === -1) out.push(list[j]);
      }
    }
    return out;
  }

  function pointToMarketControls(messagePath) {
    var controls = visibleAll(['#bm3dUp', '#bm3dDown']);
    if (controls.length) return pointToTargets(controls, messagePath);
    return pointToTarget(resolveTarget('marketControls'), messagePath);
  }

  function pointToMarketView(messagePath) {
    if (!isMarketPage()) return pointToVisibleVendingItem('view', messagePath);
    var targets = visibleAll([
      '.bm3d-stopcard.is-visible .bm3d-stopcard__viewLabel',
      '.bm3d-stopcard:not(.is-inactive) .bm3d-stopcard__viewLabel'
    ]);
    if (targets.length) return pointToTargets(targets, messagePath);
    var fallback = resolveTarget('marketProduct');
    if (fallback) return pointToTarget(fallback, messagePath);
    showTip(messagePath);
  }

  function pointToMarketAdd(messagePath) {
    if (!isMarketPage()) return pointToVisibleVendingItem('add', messagePath);
    var targets = visibleAll([
      '.bm3d-stopcard.is-visible .bm3d-stopcard__addBtn',
      '.bm3d-stopcard:not(.is-inactive) .bm3d-stopcard__addBtn'
    ]);
    if (targets.length) return pointToTargets(targets, messagePath);
    showTip(messagePath);
  }

  function pointToMarketPrice(messagePath) {
    if (!isMarketPage()) return pointToVisibleVendingItem('price', messagePath);
    var targets = visibleAll([
      '.bm3d-stopcard.is-visible .bm3d-stopcard__pill--price',
      '.bm3d-stopcard:not(.is-inactive) .bm3d-stopcard__pill--price'
    ]);
    if (targets.length) return pointToTargets(targets, messagePath);
    showTip(messagePath);
  }

  function getVendingSafeViewport() {
    var vv = window.visualViewport || null;
    var vw = vv ? vv.width : (window.innerWidth || document.documentElement.clientWidth || 360);
    var vh = vv ? vv.height : (window.innerHeight || document.documentElement.clientHeight || 640);
    var top = 0;
    var bottom = vh;
    /* The browser viewport already excludes the address bar; still keep a small
       breathing room so a half-clipped shelf is skipped in favour of the next one. */
    return { top: top + 8, bottom: bottom - 10, width: vw, height: vh };
  }

  function visibleRatioInViewport(el, vp) {
    if (!el) return 0;
    var r = el.getBoundingClientRect();
    if (!r || !r.width || !r.height) return 0;
    var top = Math.max(r.top, vp.top);
    var bottom = Math.min(r.bottom, vp.bottom);
    return Math.max(0, bottom - top) / Math.max(1, r.height);
  }

  function getBestVisibleVendingShelf() {
    var shelves = $all('.bss-shelf');
    if (!shelves.length) return null;
    var vp = getVendingSafeViewport();
    var bestPartial = null;
    var bestPartialScore = -1;

    for (var i = 0; i < shelves.length; i++) {
      var shelf = shelves[i];
      if (!isVisible(shelf)) continue;
      var row = $('.bss-shelf-row', shelf) || shelf;
      var cards = $all('.bss-card', row).filter(isVisible);
      if (!cards.length) continue;

      var rowRect = row.getBoundingClientRect();
      var shelfRect = shelf.getBoundingClientRect();
      var rowRatio = visibleRatioInViewport(row, vp);
      var shelfRatio = visibleRatioInViewport(shelf, vp);
      var topIsClean = rowRect.top >= vp.top && shelfRect.top >= vp.top - 34;
      var bottomIsClean = rowRect.bottom <= vp.bottom;

      /* First choice: the highest shelf whose product row is cleanly visible.
         This is what prevents a half-visible top row from being highlighted. */
      if (topIsClean && bottomIsClean && rowRatio >= 0.92) return shelf;

      var visiblePx = Math.max(0, Math.min(rowRect.bottom, vp.bottom) - Math.max(rowRect.top, vp.top));
      if (rowRect.bottom > vp.top && rowRect.top < vp.bottom && visiblePx > 0) {
        var score = rowRatio * 10000 - Math.max(0, rowRect.top - vp.top);
        if (shelfRatio >= 0.52 && score > bestPartialScore) {
          bestPartialScore = score;
          bestPartial = shelf;
        }
      }
    }
    return bestPartial;
  }

  function getVisibleVendingCardsFromShelf(shelf) {
    if (!shelf) return [];
    var row = $('.bss-shelf-row', shelf) || shelf;
    var vp = getVendingSafeViewport();
    return $all('.bss-card', row).filter(function (card) {
      if (!isVisible(card)) return false;
      var r = card.getBoundingClientRect();
      if (!r || !r.width || !r.height) return false;
      var horizontalOk = r.right > 10 && r.left < (vp.width - 10);
      return horizontalOk && visibleRatioInViewport(card, vp) >= 0.72;
    }).slice(0, 3);
  }

  function getBestVendingCard(cards) {
    cards = (cards || []).filter(Boolean);
    if (!cards.length) return null;
    var vp = getVendingSafeViewport();
    var bestCard = cards[0];
    var bestCardScore = -1;
    for (var i = 0; i < cards.length; i++) {
      var cr = cards[i].getBoundingClientRect();
      var cx = cr.left + cr.width / 2;
      var dx = Math.abs(cx - vp.width / 2);
      var ratio = visibleRatioInViewport(cards[i], vp);
      var score = ratio * 10000 - dx;
      if (score > bestCardScore) {
        bestCardScore = score;
        bestCard = cards[i];
      }
    }
    return bestCard;
  }

  function getVendingViewRect(card) {
    if (!card) return null;
    var cardRect = card.getBoundingClientRect();
    if (!cardRect || !cardRect.width || !cardRect.height) return null;
    var title = $('.bss-title', card);
    var add = $('.bss-add, .bss-soldout', card);
    var titleRect = title ? title.getBoundingClientRect() : null;
    var addRect = add ? add.getBoundingClientRect() : null;
    var bottom = cardRect.bottom;
    if (titleRect && titleRect.bottom > cardRect.top) bottom = Math.min(bottom, titleRect.bottom + 6);
    if (addRect && addRect.top > cardRect.top) bottom = Math.min(bottom, addRect.top - 5);
    bottom = Math.max(cardRect.top + Math.min(92, cardRect.height * 0.45), bottom);
    return {
      left: cardRect.left,
      top: cardRect.top,
      width: cardRect.width,
      height: Math.max(28, bottom - cardRect.top)
    };
  }

  function getVendingTargetFromShelf(shelf, kind) {
    var cards = getVisibleVendingCardsFromShelf(shelf);
    var bestCard = getBestVendingCard(cards);
    if (!bestCard) return null;
    if (kind === 'add') return $('.bss-add', bestCard) || bestCard;
    if (kind === 'price') return $('.bss-price', bestCard) || bestCard;
    return $('.bss-img-wrap', bestCard) || bestCard;
  }

  function createTapHand(target, kind) {
    if (!target) return null;
    var r = target.getBoundingClientRect ? target.getBoundingClientRect() : target;
    if (!r || !r.width || !r.height) return null;
    var hand = document.createElement('div');
    hand.className = 'bzch-tap-hand bzch-tap-hand--' + (kind || 'tap');
    hand.setAttribute('aria-hidden', 'true');
    hand.textContent = '👆';

    var size = 42;
    var x = r.left + r.width * (kind === 'add' ? 0.66 : 0.55) - size / 2;
    var y = r.top + r.height * (kind === 'add' ? 0.45 : 0.42) - size / 2;
    var vw = window.innerWidth || document.documentElement.clientWidth || 360;
    var vh = window.innerHeight || document.documentElement.clientHeight || 640;
    x = Math.max(8, Math.min(vw - size - 8, x));
    y = Math.max(8, Math.min(vh - size - 8, y));
    hand.style.left = Math.round(x) + 'px';
    hand.style.top = Math.round(y) + 'px';

    var mount = document.fullscreenElement || document.webkitFullscreenElement || document.body;
    try { mount.appendChild(hand); }
    catch (e) { document.body.appendChild(hand); }
    S.highlightOverlays.push(hand);
    return hand;
  }

  function createTargetOverlayRect(rect, kind, radius) {
    if (!rect || !rect.width || !rect.height) return null;
    var overlay = document.createElement('div');
    overlay.className = 'bzch-target-overlay bzch-target-overlay--market bzch-target-overlay--' + (kind || 'target');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.left = Math.round(rect.left) + 'px';
    overlay.style.top = Math.round(rect.top) + 'px';
    overlay.style.width = Math.round(rect.width) + 'px';
    overlay.style.height = Math.round(rect.height) + 'px';
    if (radius) overlay.style.borderRadius = radius;
    var mount = document.fullscreenElement || document.webkitFullscreenElement || document.body;
    try { mount.appendChild(overlay); }
    catch (e) { document.body.appendChild(overlay); }
    S.highlightOverlays.push(overlay);
    return overlay;
  }

  function pointToVisibleVendingItem(kind, messagePath) {
    var shelf = getBestVisibleVendingShelf();
    var cards = getVisibleVendingCardsFromShelf(shelf);
    if (!cards.length) {
      var fallbackCard = $('.bss-card');
      if (fallbackCard) cards = [fallbackCard];
    }
    if (!cards.length) return showTip(messagePath);

    clearPointing();
    closePanelForGuide();

    var target = null;
    var targets = [];
    var bestCard = getBestVendingCard(cards) || cards[0];

    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (kind === 'add') {
        var add = $('.bss-add', card);
        if (add && isVisible(add)) {
          targets.push(add);
          createTargetOverlay(add, 'vending-add');
        }
      } else if (kind === 'price') {
        var price = $('.bss-price', card);
        if (price && isVisible(price)) {
          targets.push(price);
          createTargetOverlay(price, 'vending-price');
        }
      } else {
        var rect = getVendingViewRect(card);
        if (rect) {
          targets.push(card);
          createTargetOverlayRect(rect, 'vending-product', '18px');
        }
      }
    }

    if (kind === 'add') target = $('.bss-add', bestCard) || targets[0] || bestCard;
    else if (kind === 'price') target = $('.bss-price', bestCard) || targets[0] || bestCard;
    else target = getVendingViewRect(bestCard) || bestCard;

    if (kind === 'view') createTapHand(target, 'view');
    else if (kind === 'add') createTapHand(target, 'add');

    S.highlightedEls = [];
    S.highlightedEl = (target && target.nodeType === 1) ? target : (bestCard || null);

    var reference = (target && target.nodeType === 1) ? target : {
      getBoundingClientRect: function () { return target; }
    };
    var pos = computePointPosition(reference);
    if (pos && S.root) {
      S.root.style.setProperty('--bzch-point-top', pos.top + 'px');
      S.root.style.setProperty('--bzch-point-left', pos.left + 'px');
      S.root.classList.remove('bzch--guide-left', 'bzch--guide-right', 'bzch--guide-top', 'bzch--guide-bottom');
      S.root.classList.add('bzch--pointing', 'bzch--guide-' + (pos.guideSide || 'left'));
      startGuideBubble(messagePath, 5);
    }

    S.pointingTimer = defer(function () { clearPointing(); }, 5200);
  }

  function pointToVendingCart(target, messagePath) {
    if (!target) return showTip(messagePath);
    clearPointing();
    closePanelForGuide();
    createTargetOverlay(target, 'vending-cart');
    S.highlightedEls = [];
    S.highlightedEl = target;
    var pos = computePointPosition(target);
    if (pos && S.root) {
      S.root.style.setProperty('--bzch-point-top', pos.top + 'px');
      S.root.style.setProperty('--bzch-point-left', pos.left + 'px');
      S.root.classList.remove('bzch--guide-left', 'bzch--guide-right', 'bzch--guide-top', 'bzch--guide-bottom');
      S.root.classList.add('bzch--pointing', 'bzch--guide-' + (pos.guideSide || 'left'));
      startGuideBubble(messagePath, 5);
    }
    S.pointingTimer = defer(function () { clearPointing(); }, 5200);
  }

  function pointToMarketProduct(messagePath) {
    return pointToMarketView(messagePath);
  }

  function resolveTarget(name) {
    if (document.getElementById('bwRoot')) {
      var worldTargets = { menu: '#bwCategories', menuFallback: '#bwCategories', menuDrawer: '#bwCategories', cart: '[data-bw-bar=cart]', search: '[data-bw-bar=search]', searchInput: '#bwSearchInput' };
      if (worldTargets[name]) return document.querySelector(worldTargets[name]);
    }
    var selectors = [];
    if (name === 'cart') selectors = isMarketPage() ? ['#bm3dCartBtn', '.cart-link'] : ['.cart-link', '[href="/cart"]', 'a[href$="/cart"]'];
    else if (name === 'checkout') selectors = ['.bzc-checkout-btn', 'button[name="checkout"]', 'a[href*="/checkout"]', '.checkout-button'];
    else if (name === 'account') selectors = ['.account-link', 'a[href*="/account"]', '[aria-label*="account" i]', '[aria-label*="λογαρια" i]'];
    else if (name === 'language') selectors = ['.locale-icon', '.locale-switcher-icon button', '[name="locale_code"]', '[aria-label*="language" i]', '[aria-label*="γλωσσα" i]', '[aria-label*="γλώσσα" i]'];
    else if (name === 'menu') selectors = isMarketPage() ? ['#bm3dMenuBtn', '#menuToggle'] : ['#menuToggle', '[aria-controls="menuDrawer"]'];
    else if (name === 'menuFallback') selectors = ['#menuToggle', '#bm3dMenuBtn'];
    else if (name === 'menuDrawer') selectors = ['#menuDrawer .bzmenu__card', '#menuDrawer a[href*="/collections"]', '#menuDrawer a', '#menuDrawer', '.bzmenu__card', '.bzmenu'];
    else if (name === 'search') selectors = ['#searchToggle', '[aria-controls="searchDrawer"]', 'input[type="search"]'];
    else if (name === 'searchInput') selectors = ['#searchInput', '#searchDrawer input[type="search"]', 'input[type="search"]'];
    else if (name === 'addToCart') selectors = ['.bzp-btn--primary', 'form[action*="/cart/add"] button[type="submit"]', 'button[name="add"]', '.product-form__submit', '[data-add-to-cart]'];
    else if (name === 'productInfo') selectors = ['.bzp-card', '.product__description', '.product-single__description', '.product-description', '.rte'];
    else if (name === 'productPopup') selectors = ['.bzp-card', '.bzp-tabs'];
    else if (name === 'marketControls') selectors = ['#bm3dControls'];
    else if (name === 'marketUp') selectors = ['#bm3dUp', '.bm3d-arrow--up'];
    else if (name === 'marketDown') selectors = ['#bm3dDown', '.bm3d-arrow--down'];
    else if (name === 'marketExit') selectors = ['.bm3d-exit-right'];
    else if (name === 'marketProduct') selectors = isMarketPage() ? ['.bm3d-stopcard.is-visible .bm3d-stopcard__viewLabel', '.bm3d-stopcard__viewLabel', '.bm3d-stop-card', '.bm3d-product-card', '.bm3d-card', '.bm3d-billboard-card'] : ['.bss-card .bss-img-wrap', '.bss-card .bss-title', '.bss-card'];

    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (isVisible(el)) return el;
    }
    return null;
  }

  function closePanelForGuide() {
    S.isOpen = false;
    if (S.panel) S.panel.hidden = true;
    if (S.button) S.button.setAttribute('aria-expanded', 'false');
    if (S.root) S.root.classList.remove('bzch--open');
  }

  function pointToTarget(target, messagePath) {
    return pointToTargets(target ? [target] : [], messagePath);
  }


  function isMarketExitTarget(el) {
    if (!el || !isMarketPage()) return false;
    try {
      return el.matches('.bm3d-exit-right, #bm3dExitBtn, [data-bm3d-exit]');
    } catch (e) {
      return false;
    }
  }

  function createTargetOverlay(target, kind) {
    if (!target) return null;
    var r = target.getBoundingClientRect();
    if (!r || !r.width || !r.height) return null;

    var overlay = document.createElement('div');
    overlay.className = 'bzch-target-overlay bzch-target-overlay--market bzch-target-overlay--' + (kind || 'target');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.left = Math.round(r.left) + 'px';
    overlay.style.top = Math.round(r.top) + 'px';
    overlay.style.width = Math.round(r.width) + 'px';
    overlay.style.height = Math.round(r.height) + 'px';

    try {
      var cs = window.getComputedStyle(target);
      if (cs && cs.borderRadius) overlay.style.borderRadius = cs.borderRadius;
    } catch (e) {}

    var mount = document.fullscreenElement || document.webkitFullscreenElement || document.body;
    try { mount.appendChild(overlay); }
    catch (e) { document.body.appendChild(overlay); }

    S.highlightOverlays.push(overlay);
    return overlay;
  }

  function pointToTargets(targets, messagePath) {
    targets = (targets || []).filter(function (el) { return !!el && isVisible(el); });
    if (!targets.length) return showTip(messagePath);
    clearPointing();

    closePanelForGuide();

    S.highlightedEls = targets.slice();
    for (var i = 0; i < S.highlightedEls.length; i++) {
      if (isMarketExitTarget(S.highlightedEls[i])) {
        createTargetOverlay(S.highlightedEls[i], 'exit');
        continue;
      }
      S.highlightedEls[i].classList.add('bzch-target-highlight');
      if (isMarketPage()) S.highlightedEls[i].classList.add('bzch-target-highlight--market');
    }
    S.highlightedEl = S.highlightedEls[0] || null;

    var reference = getUnionTarget(S.highlightedEls);
    var pos = computePointPosition(reference);
    if (pos && S.root) {
      S.root.style.setProperty('--bzch-point-top', pos.top + 'px');
      S.root.style.setProperty('--bzch-point-left', pos.left + 'px');
      S.root.classList.remove('bzch--guide-left', 'bzch--guide-right', 'bzch--guide-top', 'bzch--guide-bottom');
      S.root.classList.add('bzch--pointing', 'bzch--guide-' + (pos.guideSide || 'left'));
      startGuideBubble(messagePath, 5);
    }

    S.pointingTimer = defer(function () { clearPointing(); }, 5200);
  }

  function getUnionTarget(elements) {
    var rect = null;
    for (var i = 0; i < elements.length; i++) {
      var r = elements[i].getBoundingClientRect();
      if (!rect) rect = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
      else {
        rect.left = Math.min(rect.left, r.left);
        rect.top = Math.min(rect.top, r.top);
        rect.right = Math.max(rect.right, r.right);
        rect.bottom = Math.max(rect.bottom, r.bottom);
      }
    }
    rect = rect || { left: 0, top: 0, right: 1, bottom: 1 };
    rect.width = Math.max(1, rect.right - rect.left);
    rect.height = Math.max(1, rect.bottom - rect.top);
    return { getBoundingClientRect: function () { return rect; } };
  }

  function computePointPosition(target) {
    if (!S.button || !target) return null;
    var r = target.getBoundingClientRect();
    var size = S.button.getBoundingClientRect();
    var bw = size.width || 60;
    var bh = size.height || 60;
    var vw = window.innerWidth || document.documentElement.clientWidth || 360;
    var vh = window.innerHeight || document.documentElement.clientHeight || 640;
    var margin = 12;
    var gap = 12;

    /* Guide bubble is intentionally compact now: it only says “Here” + countdown.
       Keep positioning target-safe: the helper may move near a button, but the
       button itself should stay visible and clickable. */
    var guideW = 96;
    var guideH = 38;

    function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

    function guideBounds(left, top, side) {
      var x = left, y = top;
      if (side === 'bottom') {
        x = left + bw / 2 - guideW / 2;
        y = top + bh + 8;
      } else if (side === 'top') {
        x = left + bw / 2 - guideW / 2;
        y = top - guideH - 8;
      } else if (side === 'left') {
        x = left - guideW - 8;
        y = top + bh / 2 - guideH / 2;
      } else if (side === 'right') {
        x = left + bw + 8;
        y = top + bh / 2 - guideH / 2;
      }
      return { left: x, top: y, right: x + guideW, bottom: y + guideH };
    }

    function fits(left, top, side) {
      if (left < margin || top < margin || left + bw > vw - margin || top + bh > vh - margin) return false;
      var g = guideBounds(left, top, side);
      if (g.left < margin || g.top < margin || g.right > vw - margin || g.bottom > vh - margin) return false;
      return true;
    }

    function pack(left, top, side) {
      return { left: Math.round(left), top: Math.round(top), guideSide: side };
    }

    var centerLeft = r.left + (r.width - bw) / 2;
    var belowTop = r.bottom + gap;
    var aboveTop = r.top - bh - gap;
    var leftSide = r.left - bw - gap;
    var rightSide = r.right + gap;
    var middleTop = r.top + (r.height - bh) / 2;

    /* Header / top-bar targets (cart, language, account, search, menu): always
       prefer cloud below the target and bubble below the cloud. This prevents the
       bubble from covering the icon and keeps the top navigation visible. */
    if (r.top < Math.min(300, vh * 0.42)) {
      var l = clamp(centerLeft, margin, vw - bw - margin);
      if (fits(l, belowTop, 'bottom')) return pack(l, belowTop, 'bottom');
      /* If the cloud fits below but the bubble would clip, shift the cloud until
         the compact bubble also fits on screen. */
      var minL = margin + guideW / 2 - bw / 2;
      var maxL = vw - margin - guideW / 2 - bw / 2;
      l = clamp(centerLeft, Math.max(margin, minL), Math.min(vw - bw - margin, maxL));
      if (fits(l, belowTop, 'bottom')) return pack(l, belowTop, 'bottom');
    }

    var candidates = [
      { left: centerLeft, top: belowTop, side: 'bottom' },
      { left: centerLeft, top: aboveTop, side: 'top' },
      { left: leftSide, top: middleTop, side: 'bottom' },
      { left: rightSide, top: middleTop, side: 'bottom' },
      { left: leftSide, top: middleTop, side: 'left' },
      { left: rightSide, top: middleTop, side: 'right' }
    ];

    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      var clampedLeft = clamp(c.left, margin, vw - bw - margin);
      var clampedTop = clamp(c.top, margin, vh - bh - margin);
      if (fits(clampedLeft, clampedTop, c.side)) return pack(clampedLeft, clampedTop, c.side);
    }

    /* Last resort: keep everything inside the viewport; bubble below if possible,
       otherwise above. */
    var fallbackLeft = clamp(centerLeft, margin, vw - bw - margin);
    var fallbackTop = clamp(belowTop, margin, vh - bh - margin);
    if (fits(fallbackLeft, fallbackTop, 'bottom')) return pack(fallbackLeft, fallbackTop, 'bottom');
    fallbackTop = clamp(aboveTop, margin, vh - bh - margin);
    return pack(fallbackLeft, fallbackTop, 'top');
  }

  function clearPointing() {
    if (S.pointingTimer) {
      clearTimeout(S.pointingTimer);
      S.pointingTimer = null;
    }
    if (S.guideTickTimer) {
      clearInterval(S.guideTickTimer);
      S.guideTickTimer = null;
    }
    var highlighted = S.highlightedEls && S.highlightedEls.length ? S.highlightedEls.slice() : (S.highlightedEl ? [S.highlightedEl] : []);
    for (var i = 0; i < highlighted.length; i++) {
      try { highlighted[i].classList.remove('bzch-target-highlight', 'bzch-target-highlight--market'); } catch (e) {}
    }
    S.highlightedEls = [];
    S.highlightedEl = null;
    if (S.highlightOverlays && S.highlightOverlays.length) {
      var overlays = S.highlightOverlays.slice();
      for (var j = 0; j < overlays.length; j++) {
        try { overlays[j].remove(); } catch (e) {}
      }
      S.highlightOverlays = [];
    }
    if (S.root) {
      S.root.classList.remove('bzch--pointing', 'bzch--guide-left', 'bzch--guide-right', 'bzch--guide-top', 'bzch--guide-bottom');
      S.root.style.removeProperty('--bzch-point-top');
      S.root.style.removeProperty('--bzch-point-left');
    }
    if (S.guideEl) S.guideEl.hidden = true;
    schedulePositionCheck();
  }


  function ensureGuideBubble() {
    if (S.guideEl || !S.root) return;
    var el = document.createElement('div');
    el.id = 'bzch-guide';
    el.className = 'bzch__guide';
    el.setAttribute('aria-live', 'polite');
    el.hidden = true;

    var text = document.createElement('span');
    text.className = 'bzch__guide-text';
    var count = document.createElement('span');
    count.className = 'bzch__guide-count';

    el.appendChild(text);
    el.appendChild(count);
    S.root.appendChild(el);
    S.guideEl = el;
    S.guideTextEl = text;
    S.guideCountEl = count;
  }

  function startGuideBubble(messagePath, seconds) {
    ensureGuideBubble();
    /* Compact guide bubbles intentionally use one word only, so they never
       cover the UI or overflow on mobile. Full explanations stay in the panel. */
    var msg = t('guide.here', 'Here');
    if (S.guideTextEl) S.guideTextEl.textContent = msg;
    if (S.guideEl) S.guideEl.hidden = false;
    seconds = seconds || 5;
    S.guideUntil = Date.now() + seconds * 1000;
    updateGuideCountdown();
    if (S.guideTickTimer) clearInterval(S.guideTickTimer);
    S.guideTickTimer = setInterval(updateGuideCountdown, 250);
  }

  function updateGuideCountdown() {
    var left = Math.max(0, Math.ceil((S.guideUntil - Date.now()) / 1000));
    if (S.guideCountEl) S.guideCountEl.textContent = left ? String(left) + 's' : '';
  }

  function showOptions(result) {
    var data = result && result.optionData;
    if (!data || !Array.isArray(data.options)) return showTip('responses.unknownShort');
    if (!S.isOpen) open();
    if (S.tipEl) S.tipEl.textContent = t(data.messagePath || 'options.choosePrompt', 'Choose one:');
    renderOptionChips(data.options);
  }

  function renderOptionChips(options) {
    if (!S.chipsEl) return;
    while (S.chipsEl.firstChild) S.chipsEl.removeChild(S.chipsEl.firstChild);
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      var label = t(opt.labelPath || '', '') || opt.label || '';
      if (!label) continue;
      var el = document.createElement('button');
      el.type = 'button';
      el.className = 'bzch__chip';
      el.setAttribute('role', 'listitem');
      var labelEl = document.createElement('span');
      labelEl.className = 'bzch__chip-label';
      labelEl.textContent = label;
      el.appendChild(labelEl);
      (function (cfg) {
        el.addEventListener('click', function (ev) {
          ev.preventDefault();
          if (cfg.action === 'NAV_CATEGORY' && cfg.category) {
            var engine = intents();
            var cat = engine && engine.categories ? engine.categories[cfg.category] : null;
            handleCategory({ categoryData: cat, messagePath: cat && cat.response });
          } else if (cfg.href) {
            navigateWithTip(cfg.href, 'responses.localOnly');
          }
        });
      })(opt);
      S.chipsEl.appendChild(el);
    }
  }

  function computeMarketHref() {
    try {
      if (window.BuzzMarketHandoff && typeof window.BuzzMarketHandoff.toMinigameUrl === 'function') {
        var here = window.location.pathname + window.location.search;
        var u = window.BuzzMarketHandoff.toMinigameUrl(here);
        if (u) return u;
      }
    } catch (e) {}
    return localizeInternalHref('/pages/buzz-market');
  }


  /* ── 7.7 Intro bubble above the AI-gated helper ─────────────────────── */
  function showIntroBubble() {
    if (!S.root || isMarketPage() || S.isOpen) return;
    if (S.root.classList.contains('bzch--ai-open-hidden')) return;
    try {
      if (sessionStorage.getItem('bzch_intro_seen_v5') === '1') return;
      sessionStorage.setItem('bzch_intro_seen_v5', '1');
    } catch (e) {}

    syncAIGateNow();

    var text = t('intro.helper', 'Need help? I’m here.');
    if (!text) return;

    hideIntroBubble();
    var el = document.createElement('div');
    el.className = 'bzch__intro';
    el.setAttribute('aria-live', 'polite');
    el.textContent = text;
    S.root.appendChild(el);
    S.introEl = el;

    S.introTimer = setTimeout(hideIntroBubble, 6500);
  }

  function hideIntroBubble() {
    if (S.introTimer) {
      clearTimeout(S.introTimer);
      S.introTimer = null;
    }
    if (S.introEl && S.introEl.parentNode) {
      try { S.introEl.parentNode.removeChild(S.introEl); } catch (e) {}
    }
    S.introEl = null;
  }


  /* ── 7.8 Manual AI cover placement ──────────────────────────────── */
  function applyAIFixedCoverPosition() {
    if (!S.root) return;
    /* Final tuned values from live mobile testing.
       Keep these explicit instead of auto-detecting the AI launcher, because
       the AI app DOM changes and selector-based placement was unreliable. */
    S.root.style.setProperty('--bzch-ai-right', '2px');
    S.root.style.setProperty('--bzch-ai-bottom', '26px');
    S.root.style.setProperty('--bzch-ai-size', '64px');
  }

  /* ── 7.8 Safe AI launcher gate / market AI suppressor ───────────────── */
  function setupAIGate() {
    if (!S.root) return;

    function resync() { scheduleAISync(); }
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('resize', resync, { passive: true });
    window.addEventListener('orientationchange', resync, { passive: true });
    if (window.visualViewport && typeof window.visualViewport.addEventListener === 'function') {
      window.visualViewport.addEventListener('resize', resync);
      window.visualViewport.addEventListener('scroll', resync);
    }

    try {
      S.aiObserver = new MutationObserver(resync);
      S.aiObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'aria-hidden'] });
    } catch (e) {}

    S.aiTimer = setInterval(syncAIGateNow, 900);
    defer(syncAIGateNow, 300);
    defer(syncAIGateNow, 1200);
    defer(syncAIGateNow, 2500);
  }

  function scheduleAISync() {
    if (S.aiSyncQueued) return;
    S.aiSyncQueued = true;
    requestAnimationFrame(function () {
      S.aiSyncQueued = false;
      syncAIGateNow();
    });
  }

  function isInsideHelper(el) {
    return !!(S.root && el && (el === S.root || S.root.contains(el)));
  }

  function aiCandidateSelectors() {
    return [
      'iframe[src*="jotform" i]',
      'iframe[src*="chat" i]',
      'iframe[src*="assistant" i]',
      'iframe[src*="intercom" i]',
      'iframe[src*="crisp" i]',
      'iframe[src*="tidio" i]',
      'iframe[src*="tawk" i]',
      'iframe[src*="gorgias" i]',
      'iframe[src*="zendesk" i]',
      '[id*="jotform" i]',
      '[class*="jotform" i]',
      '[id*="jot" i]',
      '[class*="jot" i]',
      '[id*="chat" i]',
      '[class*="chat" i]',
      '[id*="assistant" i]',
      '[class*="assistant" i]',
      '[id*="chatbot" i]',
      '[class*="chatbot" i]',
      '[id*="intercom" i]',
      '[class*="intercom" i]',
      '[id*="crisp" i]',
      '[class*="crisp" i]',
      '[id*="tidio" i]',
      '[class*="tidio" i]',
      '[id*="tawk" i]',
      '[class*="tawk" i]',
      '[id*="gorgias" i]',
      '[class*="gorgias" i]',
      '[id*="zendesk" i]',
      '[class*="zendesk" i]',
      '[aria-label*="chat" i]',
      '[aria-label*="assistant" i]',
      '[aria-label*="AI" i]',
      '[title*="chat" i]',
      '[title*="assistant" i]'
    ].join(',');
  }

  function aiCandidateList() {
    var list = [];
    try { list = $all(aiCandidateSelectors()); } catch (e) { list = []; }
    return list.filter(function (el) {
      if (!el || isInsideHelper(el)) return false;
      var tag = String(el.tagName || '').toLowerCase();
      if (tag === 'script' || tag === 'style' || tag === 'link' || tag === 'meta') return false;
      return isVisible(el);
    });
  }

  function findAILauncherElement() {
    var vw = window.innerWidth || document.documentElement.clientWidth || 0;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var candidates = aiCandidateList();
    var best = null;
    var bestScore = -999999;

    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      var r = el.getBoundingClientRect();
      if (r.width < 26 || r.height < 26) continue;
      if (r.width > 220 || r.height > 220) continue; /* Large panels are open chats, not launchers. */

      var nearRight = (vw - r.right) < Math.max(220, vw * 0.40);
      var nearBottom = (vh - r.bottom) < Math.max(220, vh * 0.45);
      var score = (r.right + r.bottom) - Math.abs(r.width - r.height) * 1.5;
      if (nearRight) score += 500;
      if (nearBottom) score += 500;

      var txt = getElementSignalText(el);
      if (hasAISignal(txt)) score += 200;

      if (score > bestScore) { bestScore = score; best = el; }
    }
    return best;
  }

  function isAIConversationOpen() {
    var candidates = aiCandidateList();
    var vw = window.innerWidth || document.documentElement.clientWidth || 0;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      var r = el.getBoundingClientRect();
      if (r.width >= 190 && r.height >= 220) {
        /* Open chat panels usually live on the right/bottom and are much larger
           than the launcher. Keep the test broad enough for mobile widgets. */
        if ((vw - r.right) < Math.max(260, vw * 0.65) || (vh - r.bottom) < Math.max(260, vh * 0.65)) return true;
      }
    }
    return false;
  }

  function getElementSignalText(el) {
    if (!el) return '';
    var parts = [];
    try {
      parts.push(el.getAttribute('id') || '');
      parts.push(el.getAttribute('class') || '');
      parts.push(el.getAttribute('aria-label') || '');
      parts.push(el.getAttribute('title') || '');
      parts.push(el.getAttribute('data-testid') || '');
      parts.push(el.getAttribute('data-test-id') || '');
      parts.push(el.getAttribute('src') || '');
    } catch (e) {}
    return parts.join(' ').toLowerCase();
  }

  function hasAISignal(txt) {
    txt = String(txt || '').toLowerCase();
    return /jot|chat|assistant|chatbot|ai|intercom|crisp|tidio|tawk|gorgias|zendesk|messenger|helpdesk|support/.test(txt);
  }

  function isProtectedMarketElement(el) {
    if (!el) return true;
    if (isInsideHelper(el)) return true;
    if (el === document.body || el === document.documentElement) return true;
    try {
      if (el.closest && (el.closest('#buzz-market-3d') || el.closest('.bm3d-wrap') || el.closest('#bm3dEntryOverlay'))) return true;
      if (el.closest && el.closest('.shopify-section-group-header-group')) return true;
      if (el.closest && el.closest('pandectes-cmp')) return true;
    } catch (e) {}
    return false;
  }

  function isLikelyExternalLauncherBox(el) {
    if (!el || isProtectedMarketElement(el) || !isVisible(el)) return false;
    var r = el.getBoundingClientRect();
    var vw = window.innerWidth || document.documentElement.clientWidth || 0;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (r.width < 24 || r.height < 24) return false;
    if (r.width > Math.max(380, vw * 0.75) || r.height > Math.max(420, vh * 0.75)) return false;
    var cs = window.getComputedStyle ? window.getComputedStyle(el) : null;
    var pos = cs ? String(cs.position || '') : '';
    var z = cs ? parseInt(cs.zIndex || '0', 10) : 0;
    var nearRight = (vw - r.right) <= Math.max(240, vw * 0.42);
    var nearBottom = (vh - r.bottom) <= Math.max(240, vh * 0.42);
    var fixedish = pos === 'fixed' || pos === 'sticky';
    var signal = hasAISignal(getElementSignalText(el));
    var isFrame = String(el.tagName || '').toLowerCase() === 'iframe';
    return !!((signal || isFrame || z >= 1000) && (fixedish || nearRight || nearBottom) && (nearRight || nearBottom));
  }

  function pickExternalWidgetRoot(el) {
    var node = el;
    var best = null;
    var depth = 0;
    while (node && node !== document.body && node !== document.documentElement && depth < 8) {
      if (isProtectedMarketElement(node)) break;
      if (isLikelyExternalLauncherBox(node)) best = node;
      node = node.parentElement;
      depth++;
    }
    return best || (isLikelyExternalLauncherBox(el) ? el : null);
  }

  function collectMarketExternalAIWidgets() {
    var out = [];
    var seen = [];
    function add(el) {
      var root = pickExternalWidgetRoot(el);
      if (!root || seen.indexOf(root) !== -1 || isInsideHelper(root)) return;
      seen.push(root);
      out.push(root);
    }
    var candidates = aiCandidateList();
    for (var i = 0; i < candidates.length; i++) add(candidates[i]);

    /* Fallback for widgets with opaque class names: fixed iframes/buttons at the
       bottom/right of the screen while the Buzz Market is active. */
    var generic = [];
    try { generic = $all('iframe, button, [role="button"], [tabindex], div, aside'); } catch (e) { generic = []; }
    for (var j = 0; j < generic.length; j++) {
      var el = generic[j];
      if (!el || isProtectedMarketElement(el)) continue;
      if (isLikelyExternalLauncherBox(el)) add(el);
    }
    return out;
  }

  function hideMarketExternalAIWidgets() {
    if (!isMarketPage() && !document.getElementById('bwRoot')) return;
    var candidates = collectMarketExternalAIWidgets();
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (!el || isInsideHelper(el)) continue;
      try {
        el.setAttribute('data-bzch-market-hidden-ai', '1');
        el.setAttribute('aria-hidden', 'true');
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
      } catch (e) {}
    }
  }

  function syncAIGateNow() {
    if (!S.root) return;
    // Buzz World (now the homepage) hides the external AI launcher exactly like
    // the old minigame page did: it sat on top of the world's "Μενού" button.
    if (isMarketPage() || document.getElementById('bwRoot')) {
      S.root.classList.remove('bzch--ai-cover', 'bzch--ai-open-hidden');
      hideMarketExternalAIWidgets();
      return;
    }

    if (isAIConversationOpen()) {
      S.root.classList.add('bzch--ai-open-hidden');
      S.root.classList.remove('bzch--ai-cover');
      return;
    }
    S.root.classList.remove('bzch--ai-open-hidden');

    /* Manual AI gate positioning.
       Do not depend on detecting the Jotform/AI launcher DOM for placement.
       The Cloud Helper uses fixed bottom-right values. The click-behind action
       opens the real AI bubble underneath. */
    S.aiLauncher = null;
    applyAIFixedCoverPosition();
    S.root.classList.add('bzch--ai-cover');
  }

  /* ── 8. Buzz Market shell-aware mounting ─────────────────────────────
     In the iPhone adaptive landscape flow the minigame can rotate the whole
     .bm3d-wrap inside a portrait viewport. The helper must live inside that
     same shell, otherwise it stays in the unrotated page coordinate system and
     can appear sideways / over the controls. */
  function isMarketPage() { return !!(S.config && String(S.config.template || '') === 'page.buzz-market'); }

  function setupFullscreenMounting() {
    if (!S.root || !isMarketPage()) return;
    if (!S.originalParent) {
      S.originalParent = S.root.parentNode;
      S.originalNextSibling = S.root.nextSibling;
    }
    if (!S.fullscreenBound) {
      document.addEventListener('fullscreenchange', scheduleMarketMountSync);
      document.addEventListener('webkitfullscreenchange', scheduleMarketMountSync);
      window.addEventListener('resize', scheduleMarketMountSync, { passive: true });
      window.addEventListener('orientationchange', scheduleMarketMountSync, { passive: true });
      if (window.visualViewport && typeof window.visualViewport.addEventListener === 'function') {
        window.visualViewport.addEventListener('resize', scheduleMarketMountSync);
        window.visualViewport.addEventListener('scroll', scheduleMarketMountSync);
      }
      try {
        S.marketMountObserver = new MutationObserver(scheduleMarketMountSync);
        S.marketMountObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        if (document.body) S.marketMountObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      } catch (e) {}
      S.fullscreenBound = true;
    }
    scheduleMarketMountSync();
  }

  function getFullscreenElement() { return document.fullscreenElement || document.webkitFullscreenElement || null; }

  function getMarketShellElement() {
    return document.getElementById('buzz-market-3d') || document.querySelector('.bm3d-wrap') || null;
  }

  function scheduleMarketMountSync() {
    if (S.marketMountQueued) return;
    S.marketMountQueued = true;
    requestAnimationFrame(function () {
      S.marketMountQueued = false;
      handleFullscreenChange();
    });
  }

  function restoreHelperToOriginalParent() {
    if (!S.root || !S.originalParent) return;
    try {
      if (S.root.parentNode !== S.originalParent) {
        if (S.originalNextSibling && S.originalNextSibling.parentNode === S.originalParent) S.originalParent.insertBefore(S.root, S.originalNextSibling);
        else S.originalParent.appendChild(S.root);
      }
    } catch (e) {}
  }

  function handleFullscreenChange() {
    if (!S.root) return;
    if (!isMarketPage()) {
      restoreHelperToOriginalParent();
      S.root.classList.remove('bzch--fullscreen-mounted', 'bzch--market-shell-mounted', 'bzch--ios-adaptive-active', 'bzch--ios-fake-landscape', 'bzch--ios-native-landscape', 'bzch--compact-landscape');
      schedulePositionCheck();
      return;
    }

    var fs = getFullscreenElement();
    var shell = getMarketShellElement();
    var target = fs || shell;
    try {
      if (target && target !== S.root && !S.root.contains(target) && !target.contains(S.root)) target.appendChild(S.root);
      else if (target && target !== S.root && target.contains && !target.contains(S.root)) target.appendChild(S.root);
      else if (!target) restoreHelperToOriginalParent();
    } catch (e) {}

    var body = document.body || null;
    var root = document.documentElement || null;
    var adaptive = !!((body && body.classList.contains('bm3d-ios-adaptive-active')) || (root && root.classList.contains('bm3d-ios-adaptive-active')) || (shell && shell.classList && shell.classList.contains('bm3d-ios-adaptive-active')));
    var fake = !!((body && body.classList.contains('bm3d-ios-fake-landscape')) || (root && root.classList.contains('bm3d-ios-fake-landscape')) || (shell && shell.classList && shell.classList.contains('bm3d-ios-fake-landscape')));
    var nativeLand = !!((body && body.classList.contains('bm3d-ios-native-landscape')) || (root && root.classList.contains('bm3d-ios-native-landscape')) || (shell && shell.classList && shell.classList.contains('bm3d-ios-native-landscape')));
    var compact = !!(shell && shell.classList && shell.classList.contains('bm3d-compact-landscape'));

    S.root.classList.toggle('bzch--fullscreen-mounted', !!fs);
    S.root.classList.toggle('bzch--market-shell-mounted', !!(!fs && shell && shell.contains(S.root)));
    S.root.classList.toggle('bzch--ios-adaptive-active', adaptive);
    S.root.classList.toggle('bzch--ios-fake-landscape', fake);
    S.root.classList.toggle('bzch--ios-native-landscape', nativeLand);
    S.root.classList.toggle('bzch--compact-landscape', compact);

    /* The right side belongs to the minigame controls. In any adaptive iPhone
       mode, keep the helper on the left by default. */
    if (adaptive && !S.root.classList.contains('bzch--pointing')) S.root.classList.add('bzch--fallback-left-safe');
    schedulePositionCheck();
  }

  /* ── 9. Position fallback (minigame only) ───────────────────────────── */
  var posCheckScheduled = false;
  function schedulePositionCheck() {
    if (posCheckScheduled) return;
    posCheckScheduled = true;
    requestAnimationFrame(function () { posCheckScheduled = false; runPositionCheck(); });
  }

  function rectsOverlap(a, b) { return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom); }

  function checkOverlap(btnRect) {
    var sels = ['.bm3d-action-bar', '#bm3dControls', '.bm3d-mode-switch', '.bm3d-exit-right'];
    for (var i = 0; i < sels.length; i++) {
      var el = document.querySelector(sels[i]);
      if (!el) continue;
      var r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      if (rectsOverlap(btnRect, r)) return true;
    }
    return false;
  }

  function runPositionCheck() {
    if (!S.root || !S.button || !S.config) return;
    if (String(S.config.template || '') !== 'page.buzz-market') return;
    if (S.root.classList.contains('bzch--pointing')) return;

    S.root.classList.remove('bzch--fallback-mid-right', 'bzch--fallback-bottom-center', 'bzch--fallback-left-safe');
    requestAnimationFrame(function () {
      var btn = S.button.getBoundingClientRect();
      if (btn.width === 0 || btn.height === 0) return;
      if (!checkOverlap(btn)) return;
      /* Navigation controls own the right side. Any helper fallback stays left. */
      S.root.classList.add('bzch--fallback-left-safe');
    });
  }

  /* ── 10. Backup suppression observer ───────────────────────────────── */
  function setupSuppressionObserver() {
    var targets = [
      { selector: '.bzp-overlay', markerClass: 'is-open' },
      { selector: '.bm3d-billboard-popup', markerClass: 'is-open' },
      { selector: '.bm3d-cart-drawer', markerClass: 'is-open' },
      { selector: '.bm3d-menu-drawer', markerClass: 'is-open' }
    ];

    function entryOverlayShouldHide() {
      if (!S.config || String(S.config.template || '') !== 'page.buzz-market') return false;
      var entry = document.getElementById('bm3dEntryOverlay');
      if (!entry) return false;
      var state = String(entry.getAttribute('data-state') || '').toLowerCase();
      if (state === 'loading' || state === 'ready' || state === 'starting') return true;
      var r = entry.getBoundingClientRect();
      var style = window.getComputedStyle ? window.getComputedStyle(entry) : null;
      var visible = r.width > 0 && r.height > 0 && (!style || (style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity || '1') > 0.01));
      return visible && state !== 'entered' && state !== 'hidden' && state !== 'complete' && state !== 'done';
    }

    function check() {
      var suppress = false;
      for (var i = 0; i < targets.length; i++) {
        var el = document.querySelector(targets[i].selector);
        if (el && el.classList.contains(targets[i].markerClass)) { suppress = true; break; }
      }
      if (S.root) {
        S.root.classList.toggle('bzch--suppressed', suppress);
        S.root.classList.toggle('bzch--hidden', entryOverlayShouldHide());
      }
    }

    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () { pending = false; check(); });
    });

    try {
      observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-state'], subtree: true });
      check();
    } catch (e) {}
  }

  /* ── 11. Boot ───────────────────────────────────────────────────────── */
  onReady(function () { setTimeout(init, 0); });
})();
